import { useCallback, useMemo, useState, useEffect } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Auth } from "aws-amplify";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { ConfigProvider, theme as antdTheme } from "antd";
import { RestaurantStatus } from "@tasty/api";

// Custom Auth
import LoginPage from "./components/auth/LoginPage";

// Contexts
import { AppConfigContextProvider } from "./context/app-config-context";
import { InvoiceContextProvider } from "./context/invoice-context";
import { OrdersContextProvider } from "./context/orders-context";
import {
  RestaurantContextProvider,
  useRestaurantContext,
} from "./context/restaurant-context";
import { SettingsContextProvider } from "./context/settings-context";

// Layout Components
import { Sidebar } from "@/components/layout/Sidebar";
import { Header } from "@/components/layout/Header";
import { Toaster } from "@/components/ui/sonner";
import LoadingOverlay from "./components/loading-overlay";
import { cn } from "@/lib/utils";
import { useTheme } from "@/components/theme-provider";
import { NotificationProvider } from "@/components/notifications/NotificationProvider";

// New Tailwind Pages
import OrdersPage from "./pages/orders";
import OrderDetailPage from "./pages/order-detail";
import OrderHistoryPage from "./pages/order-history";
import MenuPage from "./pages/menu";
import SettingsPage from "./pages/settings";
import InvoicesPage from "./pages/invoices";
import BusinessHoursPage from "./pages/business-hours";
import NotFoundPage from "./pages/not-found";
import NoRestaurantPage from "./pages/no-restaurant";

// Layout Wrapper
function DashboardLayout({ onSignOut }: { onSignOut?: () => void }) {
  const { restaurant, loading, updateRestaurantStatus } = useRestaurantContext();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { resolvedTheme } = useTheme();

  const muiTheme = useMemo(() => {
    const isDark = resolvedTheme === "dark";
    return createTheme({
      palette: {
        mode: isDark ? "dark" : "light",
        primary: { main: isDark ? "#65C2C4" : "#1E2746" },
        background: isDark
          ? { default: "#0b1220", paper: "#0f172a" }
          : { default: "#ffffff", paper: "#ffffff" },
      },
      shape: { borderRadius: 12 },
    });
  }, [resolvedTheme]);

  const antdAlgorithm = resolvedTheme === "dark" ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;

  const handleStatusChange = useCallback(
    (active: boolean) => {
      updateRestaurantStatus(active ? RestaurantStatus.ACTIVE : RestaurantStatus.INACTIVE);
    },
    [updateRestaurantStatus]
  );

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/20">
      <LoadingOverlay loading={Boolean(restaurant && loading)} />

      {!loading && !restaurant ? (
        <main className="pt-14 min-h-screen flex items-center justify-center p-6">
          <NoRestaurantPage />
        </main>
      ) : (
        <>
          <Header 
            onSignOut={onSignOut} 
            userInitials={restaurant?.name?.slice(0, 2).toUpperCase() || "RD"}
            restaurantName={restaurant?.name}
            onMobileMenuClick={() => setMobileSidebarOpen(true)}
          />
          
          <Sidebar 
            restaurantName={restaurant?.name}
            restaurantStatus={restaurant?.status || ""}
            onStatusChange={handleStatusChange}
            isCollapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            isMobileOpen={mobileSidebarOpen}
            onMobileClose={() => setMobileSidebarOpen(false)}
          />

          <main
            className={cn(
              "pt-14 min-h-screen transition-all duration-300",
              sidebarCollapsed ? "md:pl-20" : "md:pl-64"
            )}
          >
            <div className="p-4 md:p-10 max-w-[1600px] mx-auto">
              <ThemeProvider theme={muiTheme}>
                <ConfigProvider
                  theme={{
                    algorithm: antdAlgorithm,
                    token: { colorPrimary: "#65C2C4", borderRadius: 12 },
                  }}
                >
                  <NotificationProvider>
                    <Outlet />
                  </NotificationProvider>
                </ConfigProvider>
              </ThemeProvider>
            </div>
          </main>
        </>
      )}
    </div>
  );
}

function AppWithProviders({ signOut }: { signOut?: () => void }) {
  return (
    <AppConfigContextProvider>
      <SettingsContextProvider>
        <RestaurantContextProvider>
          <OrdersContextProvider>
            <InvoiceContextProvider>
              <>
                <Routes>
                  <Route element={<DashboardLayout onSignOut={signOut} />}>
                    <Route path="/" element={<OrdersPage />} />
                    <Route path="/orders" element={<OrdersPage />} />
                    <Route path="/order/:id" element={<OrderDetailPage />} />
                    <Route path="/order-history" element={<OrderHistoryPage />} />
                    <Route path="/invoices" element={<InvoicesPage />} />
                    <Route path="/menu" element={<MenuPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/business-hours" element={<BusinessHoursPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                </Routes>
                <Toaster position="bottom-right" theme="system" />
              </>
            </InvoiceContextProvider>
          </OrdersContextProvider>
        </RestaurantContextProvider>
      </SettingsContextProvider>
    </AppConfigContextProvider>
  );
}

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(() => setIsAuthenticated(true))
      .catch(() => setIsAuthenticated(false))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage onSignIn={() => setIsAuthenticated(true)} />;
  }

  return <AppWithProviders signOut={async () => { await Auth.signOut(); setIsAuthenticated(false); }} />;
}
